import { HttpClient } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject, signal } from "@angular/core";
import type { MockChatConfig } from "@storebrand-digital/mock-ai-chat-widget";
import { KeycloakService } from "src/app/services/keycloak.service";
import { environment } from "src/environments/environment";
import { isOfflineModeOverrideActive } from "src/environments/offline-mode-override";
import type { PensionMockSpec } from "./ai-mock-chat-pack";

export type AiMockChatStatus = "ready" | "unavailable";

/**
 * Thin Angular wrapper around the `<mock-ai-chat>` custom element, following the widget's
 * documented Angular integration pattern: the element, the `complete` function and the domain pack
 * are all dynamic imports, so this dev-only dependency stays out of the initial bundle. They are
 * still emitted as lazy chunks in every build (this component is statically reachable through
 * `DevComponent`), but are only fetched once dev tools are enabled and offline mode is on. That
 * split is unrelated to any tab activation (there is no dev-panel tab for this widget).
 * `DevComponent` mounts this component whenever dev tools are enabled, but the chat bubble only
 * renders with offline mode on through the dev-panel override: the chat picks offline mock data, so
 * it has nothing to do online.
 *
 * With the override on, the bubble renders straight away; authentication never hides it. The live
 * AI backend needs a real Keycloak session:
 * - Online (offline mode off), nothing renders and no Keycloak call is made.
 * - Offline via the dev-panel override, the APP_INITIALIZER skips Keycloak (see
 *   `src/app/initializers/keycloak.initializer.ts`), so the component starts
 *   `KeycloakService.initSilentlyForDevTools()` (non-interactive `check-sso`) in the background as a
 *   warm-up. Each completion awaits that check; if there is no session it starts an explicit login
 *   and the widget shows an explanatory error. When authenticated, `KeycloakBearerInterceptor`
 *   attaches the token to the AI endpoint only.
 * - Offline from a build that hard-codes `offlineMode` (e2e, `environment.offlineModeHardcoded`),
 *   nothing renders and no Keycloak network call is made — even if a `dev.offlineMode` override is
 *   still stored from an earlier dev session on the same origin.
 */
@Component({
  selector: "app-ai-mock-chat",
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <!--
      Only create <mock-ai-chat> once config is set, i.e. after defineMockChatElement() has run.
      Creating it earlier makes Angular write config onto the un-upgraded element as an own
      property, which shadows the element's config setter, so the widget never mounts.
    -->
    @if (status() === "ready" && config(); as readyConfig) {
      <mock-ai-chat [config]="readyConfig"></mock-ai-chat>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        position: fixed;
        right: 24px;
        bottom: 24px;
        z-index: 9998;
      }
    `,
  ],
})
export class AiMockChatComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly keycloakService = inject(KeycloakService);

  public readonly status = signal<AiMockChatStatus>("ready");
  public readonly config = signal<MockChatConfig<PensionMockSpec> | undefined>(undefined);

  public async ngOnInit(): Promise<void> {
    // `environment.offlineMode` is what this page load runs with. The stored override alone is not
    // enough: it is only applied on the next reload.
    if (
      !environment.offlineMode ||
      environment.offlineModeHardcoded ||
      !isOfflineModeOverrideActive(environment.production)
    ) {
      this.status.set("unavailable");
      return;
    }

    // Background warm-up only; completions await the same memoised check before each request.
    this.keycloakService.initSilentlyForDevTools().catch(() => {
      console.warn("[AiMockChatComponent] Silent Keycloak check failed");
    });

    await this.loadWidget();
  }

  private startLogin(): void {
    this.keycloakService.login().catch(() => {
      console.warn("[AiMockChatComponent] Keycloak login failed");
    });
  }

  private async loadWidget(): Promise<void> {
    const [{ defineMockChatElement }, { createAiMockChatComplete }, { aiMockChatPack }] = await Promise.all([
      import("@storebrand-digital/mock-ai-chat-widget/element"),
      import("./ai-mock-chat-complete"),
      import("./ai-mock-chat-pack"),
    ]);

    defineMockChatElement();

    this.config.set({
      pack: aiMockChatPack,
      complete: createAiMockChatComplete(this.http, {
        // Re-check `authenticated()` after the memoised init: a failed token refresh clears the session.
        ensureAuthenticated: async () =>
          (await this.keycloakService.initSilentlyForDevTools()) && this.keycloakService.authenticated() === true,
        startLogin: () => this.startLogin(),
      }),
      title: "AI mock chat",
    });
  }
}
